import { useSyncExternalStore } from "react";
import { BadgeTone } from "stories/Admin";
import type { CountKey } from "pages/AdminLayout/menuConfig";

/*
 * Henvendelser i superadmin: «Kontakt oss»-meldinger, admin-søknader og
 * moskéforslag. Typer, tekster og tellerne i menyen.
 */

// ---------- Tellere ----------

export interface InquirySummary {
  unread: number;
  contactUnread: number;
  adminRequestsPending: number;
  mosqueSuggestionsNew: number;
  total: number;
  /** Janazah-opplastinger fra appene som venter på godkjenning (ikke med i total). */
  janazahPending?: number;
}

export const SUMMARY_URL = "/api/v1/superadmin/inquiries/summary";
const REFRESH_EVENT = "ibonn:inquiries-refresh";

let summary: InquirySummary | null = null;
const listeners = new Set<() => void>();

export const setInquirySummary = (next: InquirySummary | null) => {
  summary = next;
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Tellerne slik menyen sist hentet dem (null før første henting). */
export const useInquirySummary = () =>
  useSyncExternalStore(subscribe, () => summary);

export const countFor = (s: InquirySummary | null, key?: CountKey) =>
  key && s ? (s[key] ?? 0) : 0;

/** Ber menyen hente tellerne på nytt, f.eks. etter at en melding er lest. */
export const requestInquiryRefresh = () =>
  window.dispatchEvent(new Event(REFRESH_EVENT));

export const onInquiryRefresh = (handler: () => void) => {
  window.addEventListener(REFRESH_EVENT, handler);
  return () => window.removeEventListener(REFRESH_EVENT, handler);
};

// ---------- Felles ----------

/** Tidspunkt i norsk tid, uansett hvor nettleseren er. Serveren sender UTC. */
export const formatOslo = (iso: string | null | undefined) => {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "–";
  return d.toLocaleString("nb-NO", {
    timeZone: "Europe/Oslo",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Første ikke-tomme linje, kuttet til [max] tegn. */
export const firstLine = (text: string | null | undefined, max = 120) => {
  const line =
    (text ?? "")
      .split(/\r?\n/)
      .map((l) => l.trim())
      .find((l) => l.length > 0) ?? "";
  return line.length > max ? `${line.slice(0, max - 1).trimEnd()}…` : line;
};

export const platformLabel = (platform: string | null | undefined) =>
  platform === "ios"
    ? "iOS"
    : platform === "android"
      ? "Android"
      : platform === "web"
        ? "Nettsiden"
        : (platform ?? "–");

/** mailto-lenke med emnet «Svar fra iBønn». */
export const replyMailto = (email: string, subject = "Svar fra iBønn") =>
  `mailto:${encodeURIComponent(email).replace(/%40/g, "@")}?subject=${encodeURIComponent(subject)}`;

const query = (
  base: string,
  params: Record<string, string | number | undefined>,
) => {
  const s = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    const value = typeof v === "string" ? v.trim() : v;
    if (value !== undefined && value !== "") s.set(k, String(value));
  });
  return `${base}?${s.toString()}`;
};

// ---------- Kontakt oss ----------

export type ContactCategory =
  | "GENERAL"
  | "BUG"
  | "SUGGESTION"
  | "MOSQUE"
  | "OTHER";
export type ContactStatus = "NEW" | "READ" | "ARCHIVED";

export interface ContactMessage {
  id: number;
  createdAt: string;
  updatedAt: string;
  category: ContactCategory | string;
  name: string | null;
  email: string | null;
  message: string;
  mosqueOrgNr: string | null;
  mosqueName: string | null;
  platform: string;
  appVersion: string | null;
  deviceModel: string | null;
  osVersion: string | null;
  firebaseUid: string | null;
  status: ContactStatus | string;
  adminNote: string | null;
}

export interface ContactPage {
  items: ContactMessage[];
  total: number;
  page: number;
  size: number;
  unread: number;
}

export const CONTACT_BASE = "/api/v1/superadmin/contact";

export const categoryInfo: Record<string, { label: string; tone: BadgeTone }> =
  {
    GENERAL: { label: "Generelt", tone: "grey" },
    BUG: { label: "Feil", tone: "rose" },
    SUGGESTION: { label: "Forslag", tone: "gold" },
    MOSQUE: { label: "Moské", tone: "mint" },
    OTHER: { label: "Annet", tone: "lilac" },
  };

export const categoryLabel = (c: string) => categoryInfo[c]?.label ?? c;

/** «Ola (ola@x.no)», bare én av dem, eller «Anonym». */
export const senderText = (m: Pick<ContactMessage, "name" | "email">) =>
  m.name && m.email ? `${m.name} (${m.email})` : m.name || m.email || "Anonym";

export const contactQuery = (p: {
  status: string;
  category: string;
  q: string;
  page: number;
  size: number;
}) =>
  query(CONTACT_BASE, {
    status: p.status,
    category: p.category,
    q: p.q,
    page: p.page,
    size: p.size,
  });

// ---------- Admin-søknader ----------

export type AdminRequestStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface AdminRequest {
  id: number;
  createdAt: string;
  updatedAt: string;
  firebaseUid: string;
  userEmail: string | null;
  mosqueOrgNr: string;
  mosqueName: string | null;
  mosqueHasAdmin: boolean;
  fullName: string;
  role: string;
  phone: string;
  contactEmail: string;
  message: string | null;
  status: AdminRequestStatus | string;
  decidedBy: string | null;
  decidedAt: string | null;
  rejectReason: string | null;
}

export interface AdminRequestPage {
  items: AdminRequest[];
  total: number;
  page: number;
  size: number;
  pending: number;
}

export const ADMIN_REQUESTS_BASE = "/api/v1/superadmin/admin-requests";

export const adminRequestStatusInfo: Record<
  string,
  { label: string; tone: BadgeTone }
> = {
  PENDING: { label: "Venter", tone: "gold" },
  APPROVED: { label: "Godkjent", tone: "mint" },
  REJECTED: { label: "Avslått", tone: "rose" },
};

export const adminRequestsQuery = (p: {
  status: string;
  q: string;
  page: number;
  size: number;
}) =>
  query(ADMIN_REQUESTS_BASE, {
    status: p.status,
    q: p.q,
    page: p.page,
    size: p.size,
  });

// ---------- Nye moskeer ----------

export type SuggestionStatus = "NEW" | "IN_PROGRESS" | "ADDED" | "REJECTED";

export interface MosqueSuggestion {
  id: number;
  createdAt: string;
  updatedAt: string;
  name: string;
  city: string;
  address: string | null;
  website: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  isAffiliated: boolean;
  message: string | null;
  firebaseUid: string | null;
  platform: string;
  appVersion: string | null;
  status: SuggestionStatus | string;
  adminNote: string | null;
  linkedOrgNr: string | null;
  linkedMosqueName: string | null;
}

export interface MosqueSuggestionPage {
  items: MosqueSuggestion[];
  total: number;
  page: number;
  size: number;
  newCount: number;
}

export const SUGGESTIONS_BASE = "/api/v1/superadmin/mosque-suggestions";

export const suggestionStatusInfo: Record<
  string,
  { label: string; tone: BadgeTone }
> = {
  NEW: { label: "Ny", tone: "gold" },
  IN_PROGRESS: { label: "Under arbeid", tone: "blue" },
  ADDED: { label: "Lagt inn", tone: "mint" },
  REJECTED: { label: "Avvist", tone: "grey" },
};

export const suggestionsQuery = (p: {
  status: string;
  q: string;
  page: number;
  size: number;
}) =>
  query(SUGGESTIONS_BASE, {
    status: p.status,
    q: p.q,
    page: p.page,
    size: p.size,
  });

/** Felt til «Opprett moské» i Moskeer-siden (location.state.prefill). */
export interface MosquePrefill {
  name: string;
  orgName: string;
  city: string;
  streetAddress: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

export const suggestionPrefill = (s: MosqueSuggestion): MosquePrefill => ({
  name: s.name,
  orgName: s.name,
  city: s.city,
  streetAddress: s.address ?? "",
  contactName: s.contactName ?? "",
  contactPhone: s.contactPhone ?? "",
  contactEmail: s.contactEmail ?? "",
});

/** Feilmeldingen fra serveren når den finnes, ellers [fallback]. */
export const errorText = (
  error: { message?: string } | null | undefined,
  fallback: string,
) => error?.message || fallback;
